import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { countryName, fmt, formatDay, formatDayTime, formatMonth, withUnit } from '../view/format';

dayjs.extend(customParseFormat);

const present = value => value != null && value !== '';
const field = (key, label, value, sub) =>
    present(value) ? { key, label, value: String(value), sub: sub || '' } : null;
const day = date => formatDay(date);
// "Report from": when the report was made, else when it was ordered. Never the order's updatedAt, which an upgrade or a
// status change moves (SC-QA-68)
const reportDateOf = job => day((job.report && job.report.createdAt) || job.createdAt);
const month = date => formatMonth(date);
// an odometer reading of 0 or less is no reading: never shown as "0 km"
const reading = value => (Number(value) > 0 ? Number(value) : null);

// parser visit type → the tag the design shows
const TYPE_TAGS = {
    maintenance: 'Maintenance',
    service: 'Service',
    repair: 'Repair',
    bodywork: 'Bodywork / paint',
    tyres: 'Tyres',
    inspection: 'Inspection',
    'key read': 'Key read',
    key: 'Key read',
    recall: 'Recall',
    warranty: 'Warranty',
};

// litres from the parser (1.4), or the document's own wording ("1968 cm3"); 0 is no value
const engineSizeOf = value => {
    if (!present(value)) return null;
    const number = Number(value);
    if (Number.isFinite(number)) return number > 0 ? withUnit(number, 'l') : null;
    return String(value);
};

const vehicleFields = (job, report, t) =>
    [
        field('model', t('adapter.fields.model'), report.model, report.configurationCode),
        field('region', t('adapter.fields.region'), job.region),
        field(
            'engine',
            t('adapter.fields.engine'),
            report.engine || report.engineCode,
            report.engine && report.engineCode ? report.engineCode : ''
        ),
        field('engineSize', t('adapter.fields.engineSize'), engineSizeOf(report.engineSize)),
        field('fuel', t('adapter.fields.fuel'), report.fuelType),
        field('transmission', t('adapter.fields.gearbox'), report.transmissionType),
        field('drive', t('adapter.fields.drive'), report.driveType),
        field('bodyType', t('adapter.fields.bodyType'), report.bodyType),
        field('doors', t('adapter.fields.doors'), report.doorCount),
        field(
            'colour',
            t('adapter.fields.colour'),
            report.generalColor || report.numberOfCarColor,
            report.generalColor ? report.numberOfCarColor : ''
        ),
        field(
            'production',
            t('adapter.fields.production'),
            report.manufacturedDate && dayjs(report.manufacturedDate).format('YYYY-MM-DD')
        ),
        field(
            'registration',
            t('adapter.fields.registration'),
            report.registrationDate && dayjs(report.registrationDate).format('YYYY-MM-DD')
        ),
    ].filter(Boolean);

// the parser's description without an empty "Order:" tail (older reports)
const remarkOf = description =>
    String(description || '')
        .replace(/\s*Order:\s*$/i, '')
        .trim();

const refsOf = (info, t) => {
    const invoices = info.invoice_nos || (info.invoice_no ? [info.invoice_no] : []);
    return [
        info.order_no && t('adapter.refs.order', { no: info.order_no }),
        invoices.length && t('adapter.refs.invoice', { count: invoices.length, nos: invoices.join(', ') }),
    ].filter(Boolean);
};

// operations the parser read; older reports only have details (description + number), shown the same way
const opsOf = info =>
    (info.operations || []).length
        ? info.operations.map(o => ({
              code: o.code || '',
              text: o.text || o.original || '',
              original: o.text && o.original !== o.text ? o.original || '' : '',
              maint: !!o.maint,
              paint: !!o.paint,
          }))
        : (info.details || [])
              .filter(d => present(d.property))
              .map(d => ({ code: present(d.value) ? String(d.value) : '', text: String(d.property) }));

const buildVisit = (item, n, t) => {
    const info = item.info || {};
    const tags = [TYPE_TAGS[String(info.type || '').toLowerCase()], info.complaint && 'Complaint'].filter(Boolean);
    // the workshop and its country; the customer never sees a dealer number (SC-QA-60)
    const dealer = [info.dealer_name || info.dealer, countryName(info.country)].filter(Boolean).join(' · ');

    return {
        n,
        date: day(item.date),
        km: reading(info.odometer),
        tags,
        remark: remarkOf(info.description),
        complaint:
            info.complaint && (info.complaint.text || info.complaint.original)
                ? {
                      text: info.complaint.text || info.complaint.original,
                      original: info.complaint.text ? info.complaint.original : '',
                      assessment: info.complaint.assessment,
                  }
                : null,
        ops: opsOf(info),
        parts: (info.parts || []).map(p => ({
            no: p.no || '',
            text: p.text || '',
            qty: present(p.qty) ? String(p.qty) : '',
        })),
        refs: refsOf(info, t),
        dealer,
        country: countryName(info.country),
        sortDate: item.date,
    };
};

// visits newest first, numbered from the oldest
const buildVisits = (reportData, t) => {
    const items = (reportData || []).filter(item => item && item.date && dayjs(item.date).isValid());
    const oldestFirst = items.slice().sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
    return oldestFirst.map((item, i) => buildVisit(item, i + 1, t)).reverse();
};

const mileagePoints = rows =>
    rows
        .filter(row => row.km != null && row.sortDate)
        .sort((a, b) => dayjs(a.sortDate).valueOf() - dayjs(b.sortDate).valueOf())
        .map(row => {
            const d = dayjs(row.sortDate);
            return { y: d.year(), m: d.month() + 1, d: d.date(), date: month(row.sortDate), km: row.km };
        });

// replaced parts, one item per part with how often and at which readings it was replaced
const partHistory = (visits, t) => {
    const byPart = new Map();
    visits
        .slice()
        .reverse()
        .forEach(visit =>
            visit.parts.forEach(part => {
                const name = part.text || part.no;
                if (!name) return;
                const entry = byPart.get(name.toLowerCase()) || {
                    component: name,
                    status: 'Replaced',
                    times: 0,
                    events: [],
                };
                Object.assign(entry, {
                    times: entry.times + 1,
                    date: visit.date,
                    km: visit.km,
                    part: part.no,
                    qty: part.qty,
                    visit: visit.n,
                });
                if (visit.km != null) entry.events.push(visit.km);
                byPart.set(name.toLowerCase(), entry);
            })
        );
    const items = Array.from(byPart.values());

    return items.length ? { groups: [{ name: t('adapter.partsGroup'), items }] } : null;
};

// repair-table dates come as the document prints them (15/03/2024 or ISO)
const parseRepairDate = value => {
    const parsed = dayjs(String(value || '').trim(), ['DD/MM/YYYY', 'DD.MM.YYYY', 'YYYY-MM-DD'], true);
    return parsed.isValid() ? parsed : null;
};

const repairRows = repairData =>
    (Array.isArray(repairData) ? repairData : []).map(row => {
        const parsed = parseRepairDate(row.date);
        return {
            date: parsed ? day(parsed) : row.date || '',
            code: row.operationCode || '',
            dossier: row.dossierNumber || '',
            reason: row.reasonCode || '',
            op: row.componentName || '',
            km: reading(row.mileage),
            sortDate: parsed ? parsed.toDate() : null,
        };
    });

const isPast = value => present(value) && dayjs(value.length === 7 ? `${value}-28` : value).isBefore(dayjs());

// "Expired" or "Until <end>" when the end is known, else "Since <start>"
const warrantyValue = (warranty, t) => {
    if (!warranty.end) return t('adapter.status.since', { date: day(warranty.start) });
    return isPast(warranty.end) ? t('adapter.status.expired') : t('adapter.status.until', { date: day(warranty.end) });
};

const statusCards = (report, t) => {
    const due = (key, label, value) =>
        present(value) && {
            label,
            value: value.length === 7 ? month(`${value}-01`) : day(value),
            sub: t('adapter.status.perDocument'),
            tone: isPast(value) ? 'warn' : 'neutral',
            key,
        };
    const recalls = Array.isArray(report.recalls) ? report.recalls : null;
    const open = (recalls || []).filter(
        r => String(r.status || '').toLowerCase() !== 'done' && String(r.status || '').toLowerCase() !== 'closed'
    );
    const warranty = report.warranty || {};

    return [
        due('service', t('adapter.status.nextService'), report.nextService),
        due('inspection', t('adapter.status.nextInspection'), report.nextInspection),
        due('emissions', t('adapter.status.nextEmissions'), report.nextEmissionsTest),
        ...open.map(r => ({
            label: t('adapter.status.openRecall'),
            value: r.code || r.title,
            sub: r.code ? r.title || '' : '',
            tone: 'alert',
        })),
        recalls &&
            !open.length && {
                label: t('adapter.status.recalls'),
                value: t('adapter.status.noneListed'),
                sub: '',
                tone: 'neutral',
            },
        (warranty.start || warranty.end) && {
            label: t('adapter.status.warranty'),
            value: warrantyValue(warranty, t),
            sub: [
                warranty.start && warranty.end && t('adapter.status.since', { date: day(warranty.start) }),
                present(warranty.km) && withUnit(fmt(warranty.km), 'km'),
            ]
                .filter(Boolean)
                .join(' · '),
            tone: 'neutral',
        },
    ].filter(Boolean);
};

// a car serviced in several countries may have been imported: say so, with the countries from the records
const countryNotice = (visits, t) => {
    const countries = Array.from(new Set(visits.map(v => v.country).filter(Boolean)));
    return countries.length > 1
        ? [
              {
                  tone: 'warn',
                  title: t('adapter.notices.countriesTitle', { count: countries.length }),
                  text: t('adapter.notices.countriesText', { countries: countries.join(', ') }),
              },
          ]
        : [];
};

// each document once: an order can hold several copies of one file (one per processing run, 13 on one order).
// Grouped by document type before the names are translated, so two documents never merge on a shared label
const sourceFileNames = (job, report, t) => {
    const keys =
        Array.isArray(report.sourceFiles) && report.sourceFiles.length
            ? report.sourceFiles
            : (job.files || []).map(file => (file.fileName.includes('kompl') ? 'kompl' : 'report'));
    return [...new Set(keys)].map(key => t(`adapter.files.${key}`, { defaultValue: key }));
};

const statsOf = (visits, rows, components) => {
    const dated = rows
        .filter(row => row.sortDate)
        .sort((a, b) => dayjs(a.sortDate).valueOf() - dayjs(b.sortDate).valueOf());
    const withKm = dated.filter(row => row.km != null);
    const last = withKm[withKm.length - 1];
    return {
        visits: visits.length || null,
        period:
            dated.length > 1
                ? `${month(dated[0].sortDate)} – ${month(dated[dated.length - 1].sortDate)}`
                : dated.length
                  ? month(dated[0].sortDate)
                  : null,
        lastKm: last ? last.km : null,
        lastDate: last ? month(last.sortDate) : null,
        components: components || null,
    };
};

// an AI summary split into paragraphs, or "unavailable" when there is none. `basis` names what it was written from
// when that is not the whole report ('inspection': our inspection alone, on a Complete report)
export const aiFromText = (text, basis = null) => {
    const summary = typeof text === 'string' ? text.trim() : '';
    if (!summary) return { status: 'unavailable' };

    return {
        status: 'ok',
        ...(basis && { basis }),
        paragraphs: summary
            .split(/\n\s*\n/)
            .map(p => p.trim())
            .filter(Boolean),
    };
};

const aiOf = report => aiFromText(report.summaryData);

// the equipment codes from the vehicle data document; older reports (and the sample) call them characteristics_data
const equipmentOf = (report, t) => {
    const characteristics = report.characteristicsData || report.characteristics_data || [];
    if (!characteristics.length) return null;

    return {
        source: t('adapter.equipmentDocument'),
        codes: characteristics.map(c => ({ code: c.code, desc: c.description || '' })),
    };
};

// what the parser read and from which files
const docinfoOf = (job, report, t) => {
    const coverage = report.parseCoverage || {};
    const extra = present(coverage.visits_found)
        ? [
              {
                  label: t('adapter.docinfo.recordsRead'),
                  value: t('adapter.docinfo.visits', { count: coverage.visits_found }),
              },
          ]
        : [];

    return { extra, files: sourceFileNames(job, report, t) };
};

// a parsed document with no visits and no repair rows says so, instead of a page of empty sections. Parsed cars never
// offer the dealer PDF (SC-QA-39), so the panel has no actions
const emptyOf = (job, report, visits, repairs, t) =>
    visits.length || repairs.length
        ? null
        : {
              title: t('adapter.empty.title'),
              text: t('adapter.empty.text'),
              files: sourceFileNames(job, report, t),
              actions: false,
          };

/** A parsed dealer report (job.report) as a report view model */
export const fromParsedReport = (job, t) => {
    const report = job.report;
    const visits = buildVisits(report.reportData, t);
    const repairs = repairRows(report.repairData);
    const history = partHistory(visits, t);
    // a repair-table report (no visits) shows the table where the journey would be; the numbers come from the visits
    // when there are any, else from the table rows
    const rows = visits.length ? visits : repairs;

    return {
        reportDate: reportDateOf(job),
        source: t('adapter.sourceDealer'),
        vehicle: {
            vin: (report.vinCode || job.vin || '').toUpperCase(),
            brand: report.make || job.brand,
            fields: vehicleFields(job, report, t),
        },
        stats: statsOf(visits, rows, history ? history.groups[0].items.length : 0),
        status: statusCards(report, t),
        notices: countryNotice(visits, t),
        ai: aiOf(report),
        history,
        timeline: visits.length ? { visits } : null,
        repairs: repairs.length ? { rows: repairs } : null,
        mileage: { points: mileagePoints(rows) },
        equipment: equipmentOf(report, t),
        docinfo: docinfoOf(job, report, t),
        empty: emptyOf(job, report, visits, repairs, t),
    };
};

// S10: steps of the dealer request, from the job status
const waitingFor = (job, t) => {
    const status = job.status && job.status.name;
    const current = status === 'processed' || status === 'verification' ? 2 : 1;
    return {
        title: t('adapter.waiting.title'),
        requested: job.createdAt ? t('adapter.waiting.requested', { date: formatDayTime(job.createdAt) }) : '',
        current,
        steps: [
            {
                label: t('adapter.waiting.sent'),
                sub: job.createdAt ? formatDayTime(job.createdAt, { year: false }) : '',
            },
            { label: t('adapter.waiting.preparing'), sub: current === 1 ? t('adapter.waiting.inProgress') : '' },
            { label: t('adapter.waiting.ready'), sub: t('adapter.waiting.readySub') },
        ],
        note: t('adapter.waiting.note'),
    };
};

/**
 * The dealer part of a report as a view model: the parsed report, or the state that stands in for it
 * (waiting, the original PDF, a document about another car, dealer unavailable).
 * `originalUrl` is the watermarked dealer PDF; `supportHref` where "Contact support" leads.
 */
export const fromDealerJob = (job, t, { originalUrl = '', supportHref = '' } = {}) => {
    if (!job) return null;

    const state = job.reportState || (job.report ? 'ready' : 'processing');
    if (state === 'ready' && job.report) return fromParsedReport(job, t);

    const base = {
        reportDate: reportDateOf(job),
        source: t('adapter.sourceDealer'),
        vehicle: {
            vin: (job.vin || '').toUpperCase(),
            brand: job.brand,
            fields: [field('region', t('adapter.fields.region'), job.region)].filter(Boolean),
        },
    };
    const original = (tone, key) => ({
        ...base,
        original: {
            tone,
            title: t(`adapter.original.${key}Title`, { brand: job.brand }),
            text: t(`adapter.original.${key}Text`, { brand: job.brand }),
            file: t('adapter.files.report'),
            url: originalUrl,
            // in the PDF, "here is the document" becomes "it is a separate file"; a document about another car is
            // not this car's original, so its card keeps its own explanation there (SC-QA-72)
            printNote: key !== 'otherVehicle',
        },
    });

    if (state === 'no_parser') return original('info', 'noParser');
    if (state === 'could_not_read') return original('info', 'couldNotRead');
    if (state === 'other_vehicle') return original('warn', 'otherVehicle');
    if (state === 'unavailable') {
        return {
            ...base,
            outcome: {
                title: t('adapter.outcome.title'),
                text: t('adapter.outcome.text'),
                secondary: t('adapter.outcome.contact'),
                secondaryHref: supportHref,
            },
        };
    }

    return { ...base, waiting: waitingFor(job, t) };
};
